import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, KeyboardEvent, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { Campaign, CampaignTarget, InstagramAccount } from "../api/types";
import { TIMEZONE_GROUPS, timezoneLabel } from "../lib/timezones";
import PageHeader from "../components/ui/PageHeader";
import EmptyState from "../components/ui/EmptyState";
import Avatar from "../components/ui/Avatar";
import Switch from "../components/ui/Switch";
import { useAuth } from "../store/auth";
import {
  IconArrowRight,
  IconCampaigns,
  IconClock,
  IconHash,
  IconAt,
  IconPencil,
  IconPlus,
  IconRepeat,
  IconTarget,
  IconTrash,
} from "../components/ui/Icons";

const PLAN_CAMPAIGN_CAPS: Record<
  string,
  { viewStories: number; likeStories: number; likePosts: number; follow: number; comment: number }
> = {
  solo:  { viewStories: 80,  likeStories: 60,  likePosts: 30, follow: 20, comment: 0  },
  pro:   { viewStories: 150, likeStories: 100, likePosts: 60, follow: 40, comment: 15 },
  label: { viewStories: 200, likeStories: 150, likePosts: 100, follow: 60, comment: 25 },
};

const PRO_PLANS = new Set(["pro", "label"]);

const DEFAULT_NEW_CAMPAIGN = {
  name: "",
  targets: [] as CampaignTarget[],
  actionMix: {
    viewStories: true,
    likeStories: true,
    likePosts: true,
    follow: false,
    comment: false,
    dmOnFollow: true,
  },
  activeHours: { startHour: 10, endHour: 21, timezone: "UTC" },
  unfollow: {
    enabled: true,
    keepIfFollowsBack: true,
  },
  enabled: true,
  commentTemplates: [] as string[],
};

function defaultTimezone(): string {
  if (typeof Intl !== "undefined") {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) return tz;
  }
  return "UTC";
}

type ActionMixKey = keyof typeof DEFAULT_NEW_CAMPAIGN.actionMix;

export default function Campaigns() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const accountsQuery = useQuery({
    queryKey: ["instagram-accounts"],
    queryFn: async () => {
      const { data } = await api.get<{ accounts: InstagramAccount[] }>(
        "/api/instagram-accounts"
      );
      return data.accounts;
    },
  });

  const campaignsQuery = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => {
      const { data } = await api.get<{ campaigns: Campaign[] }>("/api/campaigns");
      return data.campaigns;
    },
  });

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);

  const createMutation = useMutation({
    mutationFn: async (body: any) => api.post("/api/campaigns", body),
    onSuccess: () => {
      toast.success(t("campaigns.toast.created"));
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      setShowNew(false);
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: any }) =>
      api.patch(`/api/campaigns/${id}`, body),
    onSuccess: () => {
      toast.success(t("campaigns.toast.saved"));
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      setEditing(null);
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch(`/api/campaigns/${id}`, { enabled }),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.enabled ? t("campaigns.toast.enabled") : t("campaigns.toast.disabled")
      );
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/api/campaigns/${id}`),
    onSuccess: () => {
      toast.success(t("campaigns.toast.deleted"));
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const campaigns = campaignsQuery.data || [];
  const accounts = accountsQuery.data || [];

  const summary = useMemo(() => {
    const active = campaigns.filter((c) => c.enabled).length;
    const paused = campaigns.length - active;
    const targets = campaigns.reduce((sum, c) => sum + c.targets.length, 0);
    return { active, paused, targets };
  }, [campaigns]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.campaigns")}
        title={t("campaigns.title")}
        subtitle={t("campaigns.subtitle")}
        icon={<IconCampaigns />}
        actions={
          <button
            className="btn-primary w-full justify-center sm:w-auto"
            onClick={() => setShowNew(true)}
            disabled={accounts.length === 0 || showNew}
          >
            <IconPlus size={16} />
            {t("campaigns.new")}
          </button>
        }
      />

      {campaigns.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <SummaryChip tone="emerald" label={t("campaigns.summary.active", { count: summary.active })} />
          <SummaryChip tone="slate" label={t("campaigns.summary.paused", { count: summary.paused })} />
          <SummaryChip tone="brand" label={t("campaigns.summary.targets", { count: summary.targets })} />
        </div>
      )}

      {showNew && (
        <CampaignForm
          accounts={accounts}
          initial={null}
          onCancel={() => setShowNew(false)}
          onSubmit={(values) => createMutation.mutate(values)}
        />
      )}

      {campaigns.length === 0 && !showNew ? (
        <CampaignsEmptyState
          accountsCount={accounts.length}
          onCreate={() => setShowNew(true)}
        />
      ) : (
        <div className="space-y-4">
          {campaigns.map((c) => {
            const acc = accounts.find((a) => a.id === c.igAccountId);
            return editing && editing._id === c._id ? (
              <CampaignForm
                key={c._id}
                accounts={accounts}
                initial={c}
                onCancel={() => setEditing(null)}
                onSubmit={(values) => updateMutation.mutate({ id: c._id, body: values })}
              />
            ) : (
              <CampaignCard
                key={c._id}
                campaign={c}
                account={acc}
                onEdit={() => setEditing(c)}
                onDelete={() => {
                  if (confirm(t("campaigns.confirmDelete", { name: c.name }))) {
                    deleteMutation.mutate(c._id);
                  }
                }}
                onToggleEnabled={() =>
                  toggleEnabledMutation.mutate({
                    id: c._id,
                    enabled: !c.enabled,
                  })
                }
                togglePending={toggleEnabledMutation.isPending}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 *  Small summary chips above the list.
 * ------------------------------------------------------------------------ */

function SummaryChip({
  tone,
  label,
}: {
  tone: "emerald" | "slate" | "brand";
  label: string;
}): JSX.Element {
  const styles =
    tone === "emerald"
      ? "bg-emerald-500/10 text-emerald-300 ring-emerald-400/30"
      : tone === "brand"
      ? "bg-brand-500/10 text-brand-200 ring-brand-400/30"
      : "bg-slate-500/15 text-slate-300 ring-slate-500/30";
  return (
    <span className={clsx("rounded-full px-3 py-1 text-xs font-medium ring-1", styles)}>
      {label}
    </span>
  );
}

/* --------------------------------------------------------------------------
 *  Empty state for the campaigns list.
 * ------------------------------------------------------------------------ */

function CampaignsEmptyState({
  accountsCount,
  onCreate,
}: {
  accountsCount: number;
  onCreate: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<IconCampaigns size={26} />}
      title={t("campaigns.empty.title")}
      description={t("campaigns.empty.subtitle")}
      action={
        accountsCount === 0 ? (
          <Link to="/instagram-accounts/new" className="btn-primary px-5 py-2.5">
            {t("campaigns.empty.needAccount")}
          </Link>
        ) : (
          <button className="btn-primary px-5 py-2.5" onClick={onCreate}>
            <IconPlus size={16} />
            {t("campaigns.empty.cta")}
          </button>
        )
      }
    />
  );
}

/* --------------------------------------------------------------------------
 *  Campaign card
 * ------------------------------------------------------------------------ */

interface CampaignCardProps {
  campaign: Campaign;
  account: InstagramAccount | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onToggleEnabled: () => void;
  togglePending: boolean;
}

function CampaignCard({
  campaign: c,
  account,
  onEdit,
  onDelete,
  onToggleEnabled,
  togglePending,
}: CampaignCardProps): JSX.Element {
  const { t } = useTranslation();

  const accountTargets = c.targets.filter((tg) => tg.type === "account").length;
  const hashtagTargets = c.targets.filter((tg) => tg.type === "hashtag").length;

  return (
    <article className="card space-y-5">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {account ? (
            <Avatar name={account.igUsername} size="md" variant="instagram" />
          ) : (
            <Avatar name={c.name} size="md" />
          )}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold tracking-tight text-white">{c.name}</h3>
              <span
                className={clsx(
                  "pill",
                  c.enabled
                    ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
                    : "bg-slate-500/20 text-slate-300 ring-1 ring-slate-500/30"
                )}
              >
                {c.enabled ? t("common.active") : t("common.paused")}
              </span>
            </div>
            <p className="text-sm text-slate-400">
              {account ? (
                <Link
                  to={`/activity?account=${account.id}`}
                  className="text-brand-300 hover:text-brand-200"
                >
                  @{account.igUsername}
                </Link>
              ) : (
                t("common.noAccount")
              )}
              <span className="mx-1.5 text-slate-600">·</span>
              {t("campaigns.card.targetsCount", { count: c.targets.length })}
              {(accountTargets > 0 || hashtagTargets > 0) && (
                <span className="text-slate-500">
                  {" ("}
                  {accountTargets > 0 &&
                    t(
                      accountTargets === 1
                        ? "campaigns.card.targetsBreakdownAccountsOne"
                        : "campaigns.card.targetsBreakdownAccounts",
                      { count: accountTargets }
                    )}
                  {accountTargets > 0 && hashtagTargets > 0 && ", "}
                  {hashtagTargets > 0 &&
                    t(
                      hashtagTargets === 1
                        ? "campaigns.card.targetsBreakdownHashtagsOne"
                        : "campaigns.card.targetsBreakdownHashtags",
                      { count: hashtagTargets }
                    )}
                  {")"}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-stretch sm:self-start">
          <Switch
            checked={c.enabled}
            onChange={onToggleEnabled}
            disabled={togglePending}
            ariaLabel={c.enabled ? t("campaigns.card.pauseCampaign") : t("campaigns.card.enableCampaign")}
          />
          <button
            className="btn-ghost !min-h-[36px] !px-2.5 !py-1.5"
            onClick={onEdit}
            aria-label={t("common.edit")}
            title={t("common.edit")}
          >
            <IconPencil size={16} />
          </button>
          <button
            className="btn-ghost !min-h-[36px] !px-2.5 !py-1.5 text-rose-300 hover:!bg-rose-500/10 hover:!text-rose-200"
            onClick={onDelete}
            aria-label={t("common.delete")}
            title={t("common.delete")}
          >
            <IconTrash size={16} />
          </button>
        </div>
      </header>

      {/* Stats grid */}
      <div className="grid gap-2 sm:grid-cols-3">
        <Stat
          icon={<IconClock size={14} />}
          label={t("campaigns.card.stat.active")}
          value={t("campaigns.card.stat.activeValue")}
          hint={timezoneLabel(c.activeHours.timezone)}
        />
        <Stat
          icon={<IconTarget size={14} />}
          label={t("campaigns.card.stat.targets")}
          value={String(c.targets.length)}
          hint={
            [
              accountTargets > 0
                ? t(
                    accountTargets === 1
                      ? "campaigns.card.targetsBreakdownAccountsOne"
                      : "campaigns.card.targetsBreakdownAccounts",
                    { count: accountTargets }
                  )
                : null,
              hashtagTargets > 0
                ? t(
                    hashtagTargets === 1
                      ? "campaigns.card.targetsBreakdownHashtagsOne"
                      : "campaigns.card.targetsBreakdownHashtags",
                    { count: hashtagTargets }
                  )
                : null,
            ]
              .filter(Boolean)
              .join(" · ") || ""
          }
        />
        <Stat
          icon={<IconRepeat size={14} />}
          label={t("campaigns.card.stat.unfollow")}
          value={
            c.actionMix.follow && c.unfollow.enabled
              ? t("campaigns.card.stat.unfollowValue", {
                  min: c.unfollow.minDays,
                  max: c.unfollow.maxDays,
                })
              : t("campaigns.card.stat.unfollowOff")
          }
          hint={
            c.actionMix.follow && c.unfollow.enabled && c.unfollow.keepIfFollowsBack
              ? t("campaigns.card.stat.unfollowKeepBack")
              : ""
          }
          muted={!c.actionMix.follow || !c.unfollow.enabled}
        />
      </div>

      {/* Action mix */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {t("campaigns.card.actions")}
        </p>
        <div className="flex flex-wrap gap-2">
          <ActionPill
            label={t("campaigns.card.action.viewStories")}
            on={c.actionMix.viewStories}
            cap={c.dailyCaps.viewStories}
          />
          <ActionPill
            label={t("campaigns.card.action.likeStories")}
            on={c.actionMix.likeStories}
            cap={c.dailyCaps.likeStories}
          />
          <ActionPill
            label={t("campaigns.card.action.likePosts")}
            on={c.actionMix.likePosts}
            cap={c.dailyCaps.likePosts}
          />
          <ActionPill
            label={t("campaigns.card.action.follow")}
            on={c.actionMix.follow}
            cap={c.dailyCaps.follow}
          />
          <ActionPill
            label={t("campaigns.card.action.comment")}
            on={c.actionMix.comment}
            cap={c.dailyCaps.comment}
          />
          <ActionPill
            label={t("campaigns.card.action.dmOnFollow")}
            on={c.actionMix.dmOnFollow}
            cap={null}
          />
        </div>
      </div>

      {/* Targets */}
      {c.targets.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            {t("campaigns.card.targetsHeading")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {c.targets.map((target, i) => (
              <TargetChip key={i} target={target} />
            ))}
          </div>
        </div>
      )}

      {/* Footer link */}
      {account && (
        <div className="flex items-center justify-end border-t border-slate-800/60 pt-3">
          <Link
            to={`/activity?account=${account.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 transition hover:text-brand-300"
          >
            {t("campaigns.card.viewActivity")}
            <IconArrowRight size={12} />
          </Link>
        </div>
      )}
    </article>
  );
}

function Stat({
  icon,
  label,
  value,
  hint,
  muted,
}: {
  icon: JSX.Element;
  label: string;
  value: string;
  hint?: string;
  muted?: boolean;
}): JSX.Element {
  return (
    <div
      className={clsx(
        "surface-soft transition",
        muted && "opacity-60"
      )}
    >
      <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-100">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function ActionPill({
  label,
  on,
  cap,
}: {
  label: string;
  on: boolean;
  cap: number | null;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs",
        on
          ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
          : "border-slate-700/60 bg-slate-800/40 text-slate-500"
      )}
    >
      <span
        className={clsx(
          "inline-block h-1.5 w-1.5 rounded-full",
          on ? "bg-emerald-400" : "bg-slate-500"
        )}
        aria-hidden="true"
      />
      <span className="font-medium">{label}</span>
      <span className={clsx("text-[11px]", on ? "text-emerald-300/80" : "text-slate-500")}>
        {on
          ? cap !== null
            ? t("campaigns.card.actionDailyCap", { count: cap })
            : ""
          : t("campaigns.card.actionOff")}
      </span>
    </span>
  );
}

function TargetChip({ target }: { target: CampaignTarget }): JSX.Element {
  const isHashtag = target.type === "hashtag";
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium",
        isHashtag
          ? "border-cyan-500/25 bg-cyan-500/10 text-cyan-200"
          : "border-violet-500/25 bg-violet-500/10 text-violet-200"
      )}
    >
      {isHashtag ? <IconHash size={11} /> : <IconAt size={11} />}
      {target.value}
    </span>
  );
}

/* --------------------------------------------------------------------------
 *  Campaign form
 * ------------------------------------------------------------------------ */

interface CampaignFormProps {
  accounts: InstagramAccount[];
  initial: Campaign | null;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

function CampaignForm({ accounts, initial, onCancel, onSubmit }: CampaignFormProps) {
  const { t } = useTranslation();
  const userPlan = useAuth((s) => s.user?.plan ?? "solo");
  const isPro = PRO_PLANS.has(userPlan);
  const planCaps = PLAN_CAMPAIGN_CAPS[userPlan] ?? PLAN_CAMPAIGN_CAPS.solo;

  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        igAccountId: initial.igAccountId,
        name: initial.name,
        targets: initial.targets,
        actionMix: initial.actionMix,
        activeHours: initial.activeHours,
        unfollow: initial.unfollow ?? {
          enabled: true,
          keepIfFollowsBack: true,
        },
        enabled: initial.enabled,
        commentTemplates: initial.commentTemplates,
      };
    }
    return {
      ...DEFAULT_NEW_CAMPAIGN,
      activeHours: { ...DEFAULT_NEW_CAMPAIGN.activeHours, timezone: defaultTimezone() },
      igAccountId: accounts[0]?.id || "",
    };
  });
  const [targetValue, setTargetValue] = useState("");
  const [targetType, setTargetType] = useState<"account" | "hashtag">("account");
  const [commentDraft, setCommentDraft] = useState("");

  function addTarget() {
    if (!targetValue.trim()) return;
    setForm((f) => ({
      ...f,
      targets: [
        ...f.targets,
        {
          type: targetType,
          value: targetValue.trim().replace(/^[@#]/, ""),
        },
      ],
    }));
    setTargetValue("");
  }

  function addComment() {
    if (!commentDraft.trim()) return;
    setForm((f) => ({ ...f, commentTemplates: [...f.commentTemplates, commentDraft.trim()] }));
    setCommentDraft("");
  }

  /**
   * Pressing Enter inside a sub-field (target or comment template) should add to the
   * corresponding list rather than submit the whole campaign form.
   */
  function onSubKeyDown(handler: () => void) {
    return (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handler();
      }
    };
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.igAccountId) return;
    if (initial) {
      const { igAccountId: _ignored, ...rest } = form;
      onSubmit(rest);
    } else {
      onSubmit(form);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-6 ring-1 ring-brand-400/20">
      <header className="space-y-1">
        <p className="section-eyebrow">
          {initial ? t("common.edit") : t("campaigns.new")}
        </p>
        <h3 className="text-lg font-semibold text-white">
          {initial
            ? t("campaigns.form.headingEdit", { name: initial.name })
            : t("campaigns.form.headingNew")}
        </h3>
      </header>

      <div className="grid gap-4 sm:grid-cols-2">
        {!initial && (
          <div>
            <label className="label">{t("campaigns.form.account")}</label>
            <select
              className="input"
              value={form.igAccountId}
              onChange={(e) => setForm((f) => ({ ...f, igAccountId: e.target.value }))}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  @{a.igUsername}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="label">{t("campaigns.form.name")}</label>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>
      </div>

      <div>
        <label className="label">{t("campaigns.form.targets")}</label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            className="input sm:w-32"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value as "account" | "hashtag")}
          >
            <option value="account">{t("campaigns.form.targetTypes.account")}</option>
            <option value="hashtag">{t("campaigns.form.targetTypes.hashtag")}</option>
          </select>
          <input
            className="input flex-1"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            onKeyDown={onSubKeyDown(addTarget)}
            placeholder={
              targetType === "account"
                ? t("campaigns.form.targetPlaceholderAccount")
                : t("campaigns.form.targetPlaceholderHashtag")
            }
          />
          <button type="button" className="btn-secondary" onClick={addTarget}>
            <IconPlus size={14} />
            {t("common.add")}
          </button>
        </div>
        {form.targets.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {form.targets.map((target, i) => (
              <span
                key={i}
                className={clsx(
                  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium",
                  target.type === "hashtag"
                    ? "border-cyan-500/25 bg-cyan-500/10 text-cyan-200"
                    : "border-violet-500/25 bg-violet-500/10 text-violet-200"
                )}
              >
                {target.type === "hashtag" ? <IconHash size={11} /> : <IconAt size={11} />}
                {target.value}
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      targets: f.targets.filter((_, idx) => idx !== i),
                    }))
                  }
                  className="ml-1 text-slate-400 transition hover:text-rose-300"
                  aria-label={t("common.remove")}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="label">{t("campaigns.form.actionMix")}</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {(Object.keys(form.actionMix) as ActionMixKey[]).map((key) => {
            const isProAction = key === "comment" || key === "dmOnFollow";
            const locked = isProAction && !isPro;
            return (
              <label
                key={key}
                title={locked ? t("campaigns.form.proOnlyHint") : undefined}
                className={clsx(
                  "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition",
                  locked
                    ? "cursor-not-allowed border-slate-800/50 bg-slate-950/20 opacity-50"
                    : "border-slate-800 bg-slate-950/40 hover:border-slate-700"
                )}
              >
                <input
                  type="checkbox"
                  className="accent-brand-500"
                  checked={locked ? false : form.actionMix[key]}
                  disabled={locked}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      actionMix: { ...f.actionMix, [key]: e.target.checked },
                    }))
                  }
                />
                {t(`campaigns.form.actionMixOptions.${key}`)}
                {locked && (
                  <span className="ml-auto rounded bg-brand-500/20 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-300">
                    {t("campaigns.form.proOnly")}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Plan daily caps — informational, set server-side */}
      <div className="rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3 text-sm text-slate-400">
        <span className="font-medium text-slate-300">
          {t("campaigns.form.planCaps", { plan: userPlan.toUpperCase() })}
        </span>
        {" — "}
        {t("campaigns.form.planCapsRow", {
          viewStories: planCaps.viewStories,
          likeStories: planCaps.likeStories,
          likePosts: planCaps.likePosts,
          follow: planCaps.follow,
          commentPart: isPro
            ? t("campaigns.form.planCapsComment", { comment: planCaps.comment })
            : "",
        })}
      </div>

      <div>
        <label className="label">{t("campaigns.form.timezone")}</label>
        <select
          className="input"
          value={form.activeHours.timezone}
          onChange={(e) =>
            setForm((f) => ({
              ...f,
              activeHours: { ...f.activeHours, timezone: e.target.value },
            }))
          }
        >
          {!TIMEZONE_GROUPS.some((g) =>
            g.zones.some((z) => z.id === form.activeHours.timezone)
          ) && (
            <option value={form.activeHours.timezone}>{form.activeHours.timezone}</option>
          )}
          {TIMEZONE_GROUPS.map((group) => (
            <optgroup key={group.region} label={group.region}>
              {group.zones.map((tz) => (
                <option key={tz.id} value={tz.id}>
                  {tz.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <p className="mt-2 text-xs text-slate-500">
          {t("campaigns.form.activeHoursHint")}
        </p>
      </div>

      {form.actionMix.follow && (
        <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-white">
                {t("campaigns.form.unfollow.title")}
              </h4>
              <p className="mt-1 text-xs text-slate-400">{t("campaigns.form.unfollow.help")}</p>
            </div>
            <Switch
              checked={form.unfollow.enabled}
              onChange={(next) =>
                setForm((f) => ({
                  ...f,
                  unfollow: { ...f.unfollow, enabled: next },
                }))
              }
              ariaLabel={t("common.enabled")}
            />
          </div>

          {form.unfollow.enabled && (
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-brand-500"
                checked={form.unfollow.keepIfFollowsBack}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    unfollow: { ...f.unfollow, keepIfFollowsBack: e.target.checked },
                  }))
                }
              />
              {t("campaigns.form.unfollow.keep")}
            </label>
          )}
        </div>
      )}

      {form.actionMix.comment && (
        <div>
          <label className="label">{t("campaigns.form.comments.label")}</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              className="input flex-1"
              value={commentDraft}
              onChange={(e) => setCommentDraft(e.target.value)}
              onKeyDown={onSubKeyDown(addComment)}
              placeholder={t("campaigns.form.comments.placeholder")}
            />
            <button type="button" className="btn-secondary" onClick={addComment}>
              <IconPlus size={14} />
              {t("common.add")}
            </button>
          </div>
          {form.commentTemplates.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {form.commentTemplates.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900/60 px-2 py-1 text-xs"
                >
                  {c}
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        commentTemplates: f.commentTemplates.filter((_, idx) => idx !== i),
                      }))
                    }
                    className="text-slate-400 transition hover:text-rose-300"
                    aria-label={t("common.remove")}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-slate-800 pt-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Switch
            checked={form.enabled}
            onChange={(next) => setForm((f) => ({ ...f, enabled: next }))}
            ariaLabel={t("common.enabled")}
            size="sm"
          />
          <span>{t("common.enabled")}</span>
        </label>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn-primary">
            {initial ? t("campaigns.form.submitEdit") : t("campaigns.form.submitNew")}
          </button>
        </div>
      </div>
    </form>
  );
}
